import { Component, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { SiteWorkflowDirective } from '@features/feature-workflow/directives/site-workflow.directive';

import { SitesService } from '@models/sites/sites.service';
import { SiteModel } from '@models/sites/sites.store';

import { ButtonComponent } from '@shared/components/button/button.component';
import { ErrorsTooltipComponent } from '@shared/components/errors-tooltip/errors-tooltip.component';
import {
  ConfirmationActionModalComponent,
  ConfirmationActionModalData,
} from '@shared/components/modals/confirmation-action-modal/confirmation-action-modal.components';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { OverlayService } from '@shared/services/overlay.service';

@Component({
  hostDirectives: [SiteWorkflowDirective],
  imports: [TooltipDirective, ErrorsTooltipComponent, ButtonComponent],
  selector: 'aux-remove-site-button',
  templateUrl: './remove-site-button.component.html',
})
export class RemoveSiteButtonComponent {
  overlayService = inject(OverlayService);
  router = inject(Router);
  sitesService = inject(SitesService);
  siteWorkflow = inject(SiteWorkflowDirective);

  site = input.required<SiteModel>();

  async deleteSite() {
    if (this.siteWorkflow.disabledUpdateSiteCostUI()) return;

    const site = this.site();

    if (site) {
      const ref = this.overlayService.openPopup<
        ConfirmationActionModalData,
        boolean,
        ConfirmationActionModalComponent
      >({
        data: {
          keywordToExecuteAction: 'Delete Site',
          message:
            'This will permanently delete the selected site. This action cannot be undone and will remove all associated data including visit costs.',
        },
        modal: ConfirmationActionModalComponent,
        settings: {
          header: 'Delete Site?',
          primaryButton: {
            label: 'Delete Site',
            variant: 'negative',
          },
        },
      });

      const { data: refData } = await firstValueFrom(ref.afterClosed$);

      if (refData) {
        await this.sitesService.remove(site);
        void this.router.navigate([
          `${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.INDEX}`,
        ]);
      }
    }
  }
}
